import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, User, ArrowUp } from "lucide-react";
import { HRLocaleContext } from "../hr/i18n/HRLocaleProvider";
import useHRCopy from "../hr/i18n/useHRCopy";
import { answerQuestion, getSuggestions } from "../assistant/assistantEngine";

// Site assistant, presented as a working panel rather than a marketing block:
// transcript, composer, and the answerable questions on show. Answers come
// from assistantEngine, which falls back to site content (with a link) when
// no curated FAQ answer fits.
export default function ChatBot() {
  const { locale } = useContext(HRLocaleContext);
  const t = useHRCopy().assistant;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const transcriptRef = useRef(null);

  // The greeting is rendered from copy rather than stored, so it follows a
  // language switch instead of staying in the language the page opened in.
  const transcript = [{ type: "bot", text: t.greeting }, ...messages];
  const suggestions = getSuggestions(locale);

  // Keep the newest exchange in view without yanking the whole page.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = (text) => {
    const value = (text ?? input).trim();
    if (!value) return;

    const reply = answerQuestion(value, locale);
    setMessages((prev) => [
      ...prev,
      { type: "user", text: value },
      { type: "bot", text: reply.text, link: reply.link },
    ]);
    setInput("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <section className="py-20 lg:py-24 font-hanken bg-platinum border-y border-line">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl">
          <h2 className="font-bold uppercase text-[11px] tracking-[0.13em] text-navy">
            {t.eyebrow}
          </h2>
          <h3 className="mt-3 text-3xl md:text-4xl font-extrabold text-ink tracking-[-0.02em]">
            {t.title}
          </h3>
          <p className="mt-4 text-steel text-base md:text-lg leading-relaxed">{t.body}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-12 items-start">
          {/* Conversation panel */}
          <div className="lg:col-span-8 bg-white border border-line rounded-sm flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-line">
              <span className="w-8 h-8 rounded-sm bg-royal/10 flex items-center justify-center shrink-0">
                <Bot className="h-[18px] w-[18px] text-royal" aria-hidden="true" />
              </span>
              <span className="font-semibold text-[14.5px] text-ink">{t.panelTitle}</span>
            </div>

            <div
              ref={transcriptRef}
              role="log"
              aria-live="polite"
              aria-label={t.logLabel}
              className="flex-1 min-h-[280px] max-h-[380px] overflow-y-auto bg-paper px-5 py-5 space-y-4"
            >
              {transcript.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`flex items-start gap-3 max-w-[85%] ${
                      msg.type === "user" ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span
                      className={`w-8 h-8 rounded-sm shrink-0 flex items-center justify-center ${
                        msg.type === "bot" ? "bg-royal/10" : "bg-linestrong/30"
                      }`}
                    >
                      {msg.type === "bot" ? (
                        <Bot className="h-4 w-4 text-royal" aria-hidden="true" />
                      ) : (
                        <User className="h-4 w-4 text-steel" aria-hidden="true" />
                      )}
                    </span>
                    <div
                      className={`px-4 py-2.5 text-[14.5px] leading-relaxed rounded-sm ${
                        msg.type === "bot"
                          ? "bg-white border border-line text-ink"
                          : "bg-navy text-white"
                      }`}
                    >
                      {msg.text}
                      {msg.link && (
                        <Link
                          to={msg.link.href}
                          className="group mt-2 flex w-fit items-center gap-1.5 font-semibold text-royal hover:text-navy transition-colors duration-300"
                        >
                          {msg.link.label}
                          <span
                            aria-hidden="true"
                            className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                          >
                            →
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 border-t border-line p-3 bg-white"
            >
              <input
                type="text"
                aria-label={t.inputLabel}
                className="flex-1 min-w-0 px-4 py-3 text-[14.5px] rounded-sm border border-line bg-white placeholder-steel/60 focus:outline-none focus:border-royal focus:ring-2 focus:ring-royal/30 transition"
                placeholder={t.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <button
                type="submit"
                aria-label={t.send}
                disabled={!input.trim()}
                className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-sm bg-navy text-white transition-colors hover:bg-ink disabled:bg-platinum disabled:text-steel/50"
              >
                <ArrowUp className="h-[18px] w-[18px]" aria-hidden="true" />
              </button>
            </form>
          </div>

          {/* What it can actually answer */}
          <div className="lg:col-span-4 bg-white border border-line rounded-sm p-5 sm:p-6">
            <h4 className="font-bold uppercase text-[10.5px] tracking-[0.13em] text-steel/70">
              {t.faqTitle}
            </h4>
            <ul className="mt-4 space-y-2">
              {suggestions.map((question) => (
                <li key={question}>
                  <button
                    type="button"
                    onClick={() => send(question)}
                    className="group w-full text-left flex items-start gap-2.5 rounded-sm border border-line px-4 py-3 text-[14px] leading-snug text-steel transition-colors duration-300 hover:border-royal hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-royal focus-visible:outline-offset-2"
                  >
                    <span className="flex-1">{question}</span>
                    <span
                      aria-hidden="true"
                      className="text-steel/50 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-royal"
                    >
                      →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
